import {
    Component,
    OnInit,
    ViewChild,
    AfterViewInit,
    TemplateRef
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatPaginator, MatSort, MatTableDataSource } from '@angular/material';
import { CustomerService } from '@app/shared';
import { AlertService } from '@app/shared/services/alert/alert.service';
import { ReportIntegrationService } from '@app/shared/services/reports/report-integration/report-integration.service';
import { DatePipe } from '@angular/common';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

interface QueueCustomer {
    _id: string;
    name: string;
}

interface QueueItem {
    _id: string;
    company: string;
    idQueue: string;
    queueType: string;
    customer: QueueCustomer | string;
    status: string;
    createdAt: string;
    updatedAt: string;
}

@Component({
    selector: 'app-report-integration',
    templateUrl: './report-integration.component.html',
    styleUrls: ['./report-integration.component.scss'],
    providers: [DatePipe]
})
export class ReportIntegrationComponent implements OnInit, AfterViewInit {
    @ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;
    @ViewChild(MatSort, { static: false }) sort: MatSort;

    form: FormGroup;
    dataSource: MatTableDataSource<QueueItem>;
    allQueues: QueueItem[] = [];
    customers: any[] = [];
    loading = false;

    displayedColumns: string[] = [
        'idQueue',
        'queueType',
        'customer',
        'status',
        'createdAt',
        'updatedAt',
        'actions'
    ];

    selectedQueue: QueueItem;
    queueLogs: any;
    loadingLogs = false;
    processingAction = false;

    @ViewChild('modalReprocessConfirm', { static: true })
    modalReprocessConfirm: TemplateRef<any>;

    @ViewChild('modalDeleteConfirm', { static: true })
    modalDeleteConfirm: TemplateRef<any>;

    @ViewChild('modalQueueLogs', { static: true })
    modalQueueLogs: TemplateRef<any>;

    queueTypes = [
        { value: 'orders', label: 'Pedidos' },
        {
            value: 'conference_omie_volume',
            label: 'Conferência Omie (Volume)'
        },
        {
            value: 'conference_omie_status',
            label: 'Conferência Omie (Status)'
        },
        {
            value: 'entry_invoice_qive_manifest',
            label: 'Manifesto NF Entrada (Qive)'
        },
        {
            value: 'jr_order_status_webhook',
            label: 'Webhook Status Pedido JR'
        }
    ];

    statuses = [
        { value: 'processing', label: 'Processando' },
        { value: 'finished', label: 'Finalizado' },
        { value: 'error', label: 'Erro' }
    ];

    constructor(
        private reportIntegrationService: ReportIntegrationService,
        private alertService: AlertService,
        private customerService: CustomerService,
        private datePipe: DatePipe,
        private modalService: NgbModal
    ) {
        this.dataSource = new MatTableDataSource<QueueItem>([]);
        this.createForm();
    }

    ngOnInit() {
        this.customerService.all().subscribe((customers) => {
            this.customers = customers || [];
        });
        this.loadQueues();
    }

    ngAfterViewInit() {
        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;
    }

    createForm() {
        this.form = new FormGroup({
            queueType: new FormControl(''),
            customer: new FormControl(''),
            status: new FormControl('')
        });
    }

    loadQueues() {
        this.loading = true;
        this.reportIntegrationService.getAll().subscribe(
            (queues) => {
                this.allQueues = queues || [];
                this.applyFilters();
                this.loading = false;
            },
            (responseError) => {
                this.loading = false;
                this.allQueues = [];
                this.applyFilters();
                this.alertService.error(
                    (responseError &&
                        responseError.error &&
                        responseError.error.message) ||
                        'Erro ao carregar fila de integrações.'
                );
            }
        );
    }

    onSearch() {
        this.applyFilters();
    }

    clearForm() {
        this.createForm();
        this.applyFilters();
    }

    applyFilters() {
        const { queueType, customer, status } = this.form.value;
        let filtered = [...this.allQueues];

        if (queueType) {
            filtered = filtered.filter((item) => item.queueType === queueType);
        }

        if (customer) {
            filtered = filtered.filter(
                (item) => this.getCustomerId(item.customer) === customer
            );
        }

        if (status) {
            filtered = filtered.filter((item) => item.status === status);
        }

        this.dataSource.data = filtered;

        if (this.dataSource.paginator) {
            this.dataSource.paginator.firstPage();
        }
    }

    applyFilter(filterValue: string) {
        this.dataSource.filter = filterValue.trim().toLowerCase();
        if (this.dataSource.paginator) {
            this.dataSource.paginator.firstPage();
        }
    }

    getCustomerId(customer: QueueCustomer | string): string {
        if (!customer) {
            return '';
        }

        if (typeof customer === 'object') {
            return customer._id || '';
        }

        return customer;
    }

    getCustomerName(customer: QueueCustomer | string): string {
        if (!customer) {
            return '-';
        }

        if (typeof customer === 'object' && customer.name) {
            return customer.name;
        }

        const customerId = this.getCustomerId(customer);
        const found = this.customers.find((c) => c._id === customerId);
        return found ? found.name : customerId || '-';
    }

    getQueueTypeLabel(queueType: string): string {
        const found = this.queueTypes.find((t) => t.value === queueType);
        return found ? found.label : queueType;
    }

    getStatusLabel(status: string): string {
        const found = this.statuses.find((s) => s.value === status);
        return found ? found.label : status;
    }

    formatDate(date: string): string {
        return this.datePipe.transform(date, 'dd/MM/yyyy HH:mm:ss') || '-';
    }

    toogleSearch() {
        document.querySelector('.barSearch').classList.toggle('opened');
        document
            .querySelector('.contentDetailsFilter')
            .classList.toggle('enabled');
    }

    openReprocessConfirm(row: QueueItem) {
        this.selectedQueue = row;
        this.modalService.open(this.modalReprocessConfirm, {
            keyboard: false,
            backdrop: 'static'
        });
    }

    confirmReprocess() {
        if (!this.selectedQueue || !this.selectedQueue._id) {
            return;
        }

        this.processingAction = true;
        this.reportIntegrationService
            .reprocessQueue(this.selectedQueue._id)
            .subscribe(
                (response) => {
                    this.processingAction = false;
                    this.dismissModal();
                    this.alertService.success(
                        (response && response.message) ||
                            'Item reprocessado com sucesso.'
                    );
                    this.loadQueues();
                },
                (responseError) => {
                    this.processingAction = false;
                    this.alertService.error(
                        (responseError &&
                            responseError.error &&
                            responseError.error.message) ||
                            'Erro ao reprocessar item da fila.'
                    );
                }
            );
    }

    openDeleteConfirm(row: QueueItem) {
        this.selectedQueue = row;
        this.modalService.open(this.modalDeleteConfirm, {
            keyboard: false,
            backdrop: 'static'
        });
    }

    confirmDelete() {
        if (!this.selectedQueue || !this.selectedQueue._id) {
            return;
        }

        this.processingAction = true;
        this.reportIntegrationService
            .deleteQueue(this.selectedQueue._id)
            .subscribe(
                (response) => {
                    this.processingAction = false;
                    this.dismissModal();
                    this.alertService.success(
                        (response && response.message) ||
                            'Item removido da fila com sucesso.'
                    );
                    this.loadQueues();
                },
                (responseError) => {
                    this.processingAction = false;
                    this.alertService.error(
                        (responseError &&
                            responseError.error &&
                            responseError.error.message) ||
                            'Erro ao remover item da fila.'
                    );
                }
            );
    }

    viewQueueLogs(row: QueueItem) {
        if (!row || !row._id) {
            return;
        }

        this.selectedQueue = row;
        this.queueLogs = null;
        this.loadingLogs = true;

        this.modalService.open(this.modalQueueLogs, {
            size: 'lg',
            keyboard: true,
            backdrop: 'static'
        });

        this.reportIntegrationService.getQueueLogs(row._id).subscribe(
            (response) => {
                this.loadingLogs = false;
                this.queueLogs =
                    response && Array.isArray(response.logs)
                        ? response.logs
                        : [];
            },
            (responseError) => {
                this.loadingLogs = false;
                this.dismissModal();
                this.alertService.error(
                    (responseError &&
                        responseError.error &&
                        responseError.error.message) ||
                        'Erro ao carregar logs da fila.'
                );
            }
        );
    }

    dismissModal() {
        this.modalService.dismissAll();
        this.selectedQueue = null;
        this.queueLogs = null;
        this.processingAction = false;
    }
}
